<script module lang="ts">
	export enum CheckboxSize {
		SMALL,
		MEDIUM,
		LARGE
	}
</script>

<script lang="ts">
	import { Icon, HighlightedText } from '@repo/commons/components'
	import { generateId } from '@repo/commons/utils'

	interface Props {
		id?: string
		checked?: boolean
		indeterminate?: boolean
		error?: string
		label?: string
		ariaLabel?: string
		disabled?: boolean
		externalStyle?: string
		labelExternalStyle?: string
		highlightPattern?: string
		size?: CheckboxSize
		required?: boolean
		hint?: string
		onHighlightMatch?: (match: string, isExactMatch: boolean) => void
		onchange?: ((checked: boolean, id: number) => void) | null
		onclick?: ((e: MouseEvent) => void) | null
		onblur?: (() => void) | null
	}

	let {
		id = undefined,
		checked = $bindable(false),
		indeterminate = false,
		error = '',
		label = '',
		ariaLabel = undefined,
		disabled = false,
		externalStyle = '',
		labelExternalStyle = '',
		highlightPattern,
		size = CheckboxSize.MEDIUM,
		required = false,
		hint = '',
		onchange = null,
		onclick = null,
		onblur = null,
		onHighlightMatch = () => {}
	}: Props = $props()

	const checkboxId = `checkbox-${id ?? generateId()}`
	let inputElement: HTMLInputElement

	$effect(() => {
		if (inputElement) {
			inputElement.indeterminate = indeterminate
		}
	})

	function handleCheckboxChange(event: Event): void {
		event.stopPropagation()
		const target = event.currentTarget as HTMLInputElement
		checked = target.checked
		onchange?.(checked, id)
	}
</script>

<div class="checkbox-wrapper">
	<label
		for={checkboxId}
		class:disabled
		class="checkbox-container {size === CheckboxSize.SMALL
			? 'checkbox-container-small'
			: size === CheckboxSize.MEDIUM
				? 'checkbox-container-medium'
				: 'checkbox-container-large'}"
		style={labelExternalStyle}
	>
		<input
			bind:this={inputElement}
			id={checkboxId}
			type="checkbox"
			class="checkbox-native-input"
			aria-label={ariaLabel}
			bind:checked
			onchange={handleCheckboxChange}
			{onclick}
			{onblur}
			{disabled}
		/>

		<div
			class="checkbox-visual {size === CheckboxSize.SMALL
				? 'checkbox-visual-small'
				: size === CheckboxSize.MEDIUM
					? 'checkbox-visual-medium'
					: 'checkbox-visual-large'}"
			class:checked={checked || indeterminate}
			class:indeterminate
			class:disabled
			style={externalStyle}
		>
			{#if checked && !indeterminate}
				<Icon name="checkmark" size="sm" />
			{:else if indeterminate}
				<Icon name="minus" size="sm" />
			{/if}
		</div>

		{#if label}
			<span
				class="checkbox-label"
				class:checkbox-label-small={size === CheckboxSize.SMALL}
				class:checkbox-label-medium={size === CheckboxSize.MEDIUM}
				class:checkbox-label-large={size === CheckboxSize.LARGE}
			>
				{#if highlightPattern}
					<HighlightedText pattern={highlightPattern} text={label} {onHighlightMatch} />
				{:else}
					{label}
				{/if}
			</span>
		{/if}

		{#if hint}
			{#await import('@repo/commons/components') then { Tooltip }}
				<Tooltip text={hint}>
					<Icon name="info" size="sm" class="text-brand" />
				</Tooltip>
			{/await}
		{/if}

		{#if error}
			<span class="checkbox-error">{error}</span>
		{/if}
	</label>
</div>

<style lang="postcss">
	.checkbox-wrapper {
		@apply flex flex-col gap-1;
	}

	.checkbox-container {
		@apply inline-flex items-center gap-3 select-none relative;
	}

	.checkbox-container:not(.disabled) {
		@apply cursor-pointer;
	}
	.checkbox-container.disabled {
		@apply cursor-not-allowed;
	}

	.checkbox-container-small {
		@apply h-6;
	}

	.checkbox-container-medium {
		@apply h-6;
	}

	.checkbox-container-large {
		@apply h-10;
	}

	/* Visually hidden real input */
	.checkbox-native-input {
		@apply absolute opacity-0 w-0 h-0;
	}

	.checkbox-visual {
		@apply flex items-center justify-center border border-solid border-[#002c4d]/50 rounded bg-white text-white transition-colors;
	}

	.checkbox-visual.checked {
		@apply border-[#007ACC] bg-[#007ACC];
	}

	.checkbox-visual.indeterminate {
		@apply border-[#007ACC] bg-[#007ACC];
	}

	.checkbox-visual.disabled {
		@apply opacity-40;
	}

	.checkbox-visual-small {
		@apply w-6 h-6;
	}

	.checkbox-visual-medium {
		@apply w-6 h-6;
	}

	.checkbox-visual-large {
		@apply w-8 h-8;
	}

	.checkbox-label {
		@apply text-[#001526]/90 font-normal leading-normal;
	}

	.checkbox-label-small {
		@apply text-sm;
	}

	.checkbox-label-medium {
		@apply text-base;
	}

	.checkbox-label-large {
		@apply text-lg;
	}

	.checkbox-error {
		@apply text-[#e4485b] text-sm;
	}
</style>
